import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { useDPIAData } from '../../../../hooks/useDPIAData';
import { setDPIAField } from '../../../../store/slices/documentationSlice';
import { ReportFooter } from '../../../questionnaire/Report/ReportFooter/ReportFooter';
import { ReportHeader } from '../../../questionnaire/Report/ReportHeader/ReportHeader';
import { Divider } from '../../../ui/Divider/Divider';
import { DynamicFieldGroup } from '../../../ui/DynamicFieldGroup/DynamicFieldGroup';
import { RichTextArea } from '../../../ui/EditableInput/RichTextArea';
import { H2 } from '../../../ui/H2/H2';
import { Input } from '../../../ui/Input/Input';
import DatePicker from '../../../ui/DatePicker/DatePicker';
import {
  consultationAndPriorConsultationFields,
  controllerAndAssessmentIdentificationFields,
  descriptionOfProcessingOperationsAndPurposesFields,
  DPIA_FINAL_DECISION_KEY,
  DPIA_OVERALL_ASSESSMENT_KEY,
  finalConclusionsAndApprovalFields,
  finalDecisionOptions,
  MEASURES_TO_ADDRESS_RISKS_KEY,
  measuresToAddressRisksFields,
  necessityAndProportionalityFields,
  overallAssessmentOptions,
  RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY,
  riskAssessmentAndMitigationMeasuresFields
} from './DPIA.const';
import {
  DPIAMeasuresToAddressRisksItem,
  DPIARiskAssessmentItem,
  Levels
} from '../../../../types/DPIA.types';
import RadioButtonGroup from '../../../ui/RadioButton/RadioButtonGroup';
import { CalendarIcon } from '../../../../icons/SvgIcons';
import { EMAIL_REGEX } from '../../../../constants/Validation.constants';

export const DPIA = () => {
  const { headerData, footerData } = useDPIAData();
  const dispatch = useAppDispatch();
  const { dpiaFields, createDPIAResponse } = useAppSelector(
    (state) => state.documentation
  );

  const handleValueChange = (key: string, value: string, section: string) => {
    dispatch(setDPIAField({ label: key, value, section }));
  };

  const handleRadioGroupChange = (
    storageKey: string,
    selectedKey: string,
    section: string
  ) => {
    dispatch(
      setDPIAField({
        label: storageKey,
        value: selectedKey,
        section
      })
    );
  };

  const getEmailError = (type: string | undefined, value: string) =>
    type === 'email' && value.length > 0 && !EMAIL_REGEX.test(value)
      ? 'Please enter a valid email address'
      : null;

  const getStringValue = (fieldValue: any): string => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.join(',\n');
    }

    if (typeof fieldValue === 'string') {
      try {
        const parsed = JSON.parse(fieldValue);
        if (Array.isArray(parsed)) {
          return parsed.join(',\n');
        }
      } catch (error) {
        return fieldValue;
      }
    }

    return fieldValue || '';
  };

  const getRadioButtonValue = (radioObj: Levels | string): string => {
    if (typeof radioObj === 'string') {
      return radioObj;
    }

    if (typeof radioObj === 'object' && radioObj !== null) {
      if (radioObj.high) return 'high';
      if (radioObj.medium) return 'medium';
      if (radioObj.low) return 'low';
    }

    return '';
  };

  const riskAssessmentAndMitigationInitialValue = useMemo((): Record<
    string,
    string
  >[] => {
    const riskJson =
      dpiaFields[RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY]?.value;
    if (riskJson) {
      try {
        const risks = JSON.parse(riskJson) as DPIARiskAssessmentItem[];
        return risks.map((risk) => ({
          'Risk ID': risk.riskID || '',
          'Risk description': risk.riskDescription || '',
          'Affected data subjects': risk.affectedDataSubjects || '',
          Likelihood: getRadioButtonValue(
            risk.likelihoodOfOccurrence || {
              low: false,
              medium: false,
              high: false
            }
          ),
          Severity: getRadioButtonValue(
            risk.severity || { low: false, medium: false, high: false }
          ),
          'Initial Risk Level': getRadioButtonValue(
            risk.initialRiskLevel || { low: false, medium: false, high: false }
          ),
          'Existing controls': risk.existingControls || ''
        }));
      } catch (error) {
        console.error('Failed to parse risk scenarios:', error);
      }
    } else if (
      createDPIAResponse?.riskAssessmentRightsAndFreedomsOfDataSubjects
        ?.rightsAndFreedomsOfDataSubjects
    ) {
      return createDPIAResponse.riskAssessmentRightsAndFreedomsOfDataSubjects.rightsAndFreedomsOfDataSubjects.map(
        (risk: DPIARiskAssessmentItem) => ({
          'Risk ID': risk.riskID || '',
          'Risk description': risk.riskDescription || '',
          'Affected data subjects': risk.affectedDataSubjects || '',
          Likelihood: getRadioButtonValue(
            risk.likelihoodOfOccurrence || {
              low: false,
              medium: false,
              high: false
            }
          ),
          Severity: getRadioButtonValue(
            risk.severity || { low: false, medium: false, high: false }
          ),
          'Initial Risk Level': getRadioButtonValue(
            risk.initialRiskLevel || { low: false, medium: false, high: false }
          ),
          'Existing controls': risk.existingControls || ''
        })
      );
    }
    return [];
  }, [
    dpiaFields[RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY]?.value,
    createDPIAResponse?.riskAssessmentRightsAndFreedomsOfDataSubjects
      ?.rightsAndFreedomsOfDataSubjects
  ]);

  const handleRiskAssessmentAndMitigation = (
    rows: Record<string, string>[]
  ) => {
    const riskAssessment: DPIARiskAssessmentItem[] = rows
      .filter(
        (row) =>
          row['Risk ID'] ||
          row['Risk description'] ||
          row['Affected data subjects'] ||
          row['Likelihood'] ||
          row['Severity'] ||
          row['Initial Risk Level'] ||
          row['Existing controls']
      )
      .map((row) => ({
        riskID: row['Risk ID'] || '',
        riskDescription: row['Risk description'] || '',
        affectedDataSubjects: row['Affected data subjects'] || '',
        likelihoodOfOccurrence: {
          low: row['Likelihood'] === 'low',
          medium: row['Likelihood'] === 'medium',
          high: row['Likelihood'] === 'high'
        },
        severity: {
          low: row['Severity'] === 'low',
          medium: row['Severity'] === 'medium',
          high: row['Severity'] === 'high'
        },
        initialRiskLevel: {
          low: row['Initial Risk Level'] === 'low',
          medium: row['Initial Risk Level'] === 'medium',
          high: row['Initial Risk Level'] === 'high'
        },
        existingControls: row['Existing controls'] || ''
      }));

    dispatch(
      setDPIAField({
        label: RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY,
        value: JSON.stringify(riskAssessment),
        section: 'Risk Assessment for Rights and Freedoms of Data Subjects'
      })
    );
  };

  const getSelectedRiskLevel = (residualRisk: any): string => {
    if (typeof residualRisk === 'string') {
      return residualRisk;
    }

    if (typeof residualRisk === 'object' && residualRisk !== null) {
      if (residualRisk.high) return 'high';
      if (residualRisk.medium) return 'medium';
      if (residualRisk.low) return 'low';
    }

    return '';
  };

  const measuresToAddressRisksInitialValue = useMemo((): Record<
    string,
    string
  >[] => {
    const riskJson = dpiaFields[MEASURES_TO_ADDRESS_RISKS_KEY]?.value;
    if (riskJson) {
      try {
        const risks = JSON.parse(riskJson) as DPIAMeasuresToAddressRisksItem[];
        return risks.map((risk) => ({
          'Risk ID': risk.riskID || '',
          'Mitigation measure(s)': risk.mitigationMeasures || '',
          Type: risk.type || '',
          'Owner / responsible role': risk.ownerOrResponsibleRole || '',
          'Implementation timeline': risk.implementationTimeline || '',
          'Residual risk': getSelectedRiskLevel(risk.residualRisk)
        }));
      } catch (error) {
        console.error('Failed to parse risk scenarios:', error);
      }
    } else if (
      createDPIAResponse?.measuresToAddressRisks?.measuresToAddressRisks
    ) {
      return createDPIAResponse.measuresToAddressRisks.measuresToAddressRisks.map(
        (risk: DPIAMeasuresToAddressRisksItem) => ({
          'Risk ID': risk.riskID || '',
          'Mitigation measure(s)': risk.mitigationMeasures || '',
          Type: risk.type || '',
          'Owner / responsible role': risk.ownerOrResponsibleRole || '',
          'Implementation timeline': risk.implementationTimeline || '',
          'Residual risk': getSelectedRiskLevel(risk.residualRisk)
        })
      );
    }
    return [];
  }, [
    dpiaFields[MEASURES_TO_ADDRESS_RISKS_KEY]?.value,
    createDPIAResponse?.measuresToAddressRisks?.measuresToAddressRisks
  ]);

  const handleMeasuresToAddressRisksChange = (
    rows: Record<string, string>[]
  ) => {
    const measures: DPIAMeasuresToAddressRisksItem[] = rows
      .filter(
        (row) =>
          row['Risk ID']?.trim() ||
          row['Mitigation measure(s)']?.trim() ||
          row['Type']?.trim() ||
          row['Owner / responsible role']?.trim() ||
          row['Implementation timeline']?.trim() ||
          row['Residual risk']?.trim()
      )
      .map((row) => ({
        riskID: row['Risk ID'] || '',
        mitigationMeasures: row['Mitigation measure(s)'] || '',
        type: row['Type'] || '',
        ownerOrResponsibleRole: row['Owner / responsible role'] || '',
        implementationTimeline: row['Implementation timeline'] || '',
        residualRisk: {
          low: row['Residual risk'] === 'low',
          medium: row['Residual risk'] === 'medium',
          high: row['Residual risk'] === 'high'
        }
      }));

    dispatch(
      setDPIAField({
        label: MEASURES_TO_ADDRESS_RISKS_KEY,
        value: JSON.stringify(measures),
        section: 'Measures to Address Risks'
      })
    );
  };

  const mapFieldsToDynamicGroup = (fields: readonly any[]) => {
    return fields.map((field) => ({
      label: field.label,
      placeholder: field.placeholder,
      type: field.type || 'text',
      ...(field.options && { options: field.options })
    }));
  };

  return (
    <div className="w-full h-full p-5">
      <ReportHeader componentInfo={headerData} />
      <Divider classes="my-[30px]" />
      {/* Controller and Assessment Identification */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2
          title={controllerAndAssessmentIdentificationFields.title}
          textSize="text-base"
        />
        <Divider />
        {controllerAndAssessmentIdentificationFields.fields.map((field) => {
          const fieldValue = dpiaFields[field.key]?.value || '';

          return (
            <Input
              type={field.type === 'email' ? 'email' : 'text'}
              key={field.label}
              id={field.label}
              label={field.label}
              maxLength={255}
              onChange={(e) =>
                handleValueChange(
                  field.key,
                  e.target.value,
                  controllerAndAssessmentIdentificationFields.title
                )
              }
              value={fieldValue}
              placeholder={field.placeholder}
              labelClassName="label"
              inputClassName="input"
              error={getEmailError(field.type, fieldValue)}
            />
          );
        })}
        <H2
          title={controllerAndAssessmentIdentificationFields.dpo.title}
          textSize="text-base"
        />
        {controllerAndAssessmentIdentificationFields.dpo.fields.map((field) => {
          const fieldValue = dpiaFields[field.key]?.value || '';

          return (
            <Input
              type={field.type === 'email' ? 'email' : 'text'}
              key={field.label}
              id={field.label}
              label={field.label}
              maxLength={255}
              onChange={(e) =>
                handleValueChange(
                  field.key,
                  e.target.value,
                  controllerAndAssessmentIdentificationFields.dpo.title
                )
              }
              value={fieldValue}
              placeholder={field.placeholder}
              labelClassName="label"
              inputClassName="input"
              error={getEmailError(field.type, fieldValue)}
            />
          );
        })}

        <Divider classes="my-[30px]" />

        {/* DPO / Data Protection Contact (if applicable) */}
        <div className="grid grid-cols-2 gap-4">
          {controllerAndAssessmentIdentificationFields.dpo.groupfields.map(
            (field) => {
              const fieldValue = dpiaFields[field.key]?.value || '';

              if (field.type === 'date') {
                const parsedDate = fieldValue ? new Date(fieldValue) : null;
                const validStartDate =
                  parsedDate && !Number.isNaN(parsedDate.getTime())
                    ? parsedDate
                    : null;

                return (
                  <div
                    key={field.key}
                    className="flex flex-col items-start gap-1 self-stretch"
                  >
                    <label htmlFor={field.key} className="label">
                      {field.label}
                    </label>
                    <DatePicker
                      singleDate
                      className="w-full"
                      icon={CalendarIcon()}
                      placeholder={field.placeholder}
                      initialRange={{
                        startDate: validStartDate,
                        endDate: null
                      }}
                      onDateRangeChange={(range) => {
                        const selected = range.startDate;
                        handleValueChange(
                          field.key,
                          selected ? selected.toISOString().split('T')[0] : '',
                          controllerAndAssessmentIdentificationFields.dpo.title
                        );
                      }}
                    />
                  </div>
                );
              }

              return (
                <Input
                  type="text"
                  key={field.label}
                  id={field.label}
                  label={field.label}
                  maxLength={255}
                  onChange={(e) =>
                    handleValueChange(
                      field.key,
                      e.target.value,
                      controllerAndAssessmentIdentificationFields.dpo.title
                    )
                  }
                  value={fieldValue}
                  placeholder={field.placeholder}
                  labelClassName="label"
                  inputClassName="input"
                />
              );
            }
          )}
        </div>
      </div>
      <Divider classes="my-[30px]" />
      {/* Description of Processing Operations & Purposes */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2
          title={descriptionOfProcessingOperationsAndPurposesFields.title}
          textSize="text-base"
        />
        <Divider />
        {descriptionOfProcessingOperationsAndPurposesFields.fields.map(
          (field) => {
            const fieldValue = dpiaFields[field.key]?.value;

            return (
              <RichTextArea
                key={field.key}
                label={field.label}
                placeholder={field.placeholder}
                onChange={(value) =>
                  handleValueChange(
                    field.key,
                    value,
                    descriptionOfProcessingOperationsAndPurposesFields.title
                  )
                }
                value={getStringValue(fieldValue)}
              />
            );
          }
        )}
      </div>
      <Divider classes="my-[30px]" />
      {/* Necessity and Proportionality */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2
          title={necessityAndProportionalityFields.title}
          textSize="text-base"
        />
        <Divider />
        {necessityAndProportionalityFields.fields.map((field) => (
          <RichTextArea
            key={field.key}
            label={field.label}
            placeholder={field.placeholder}
            onChange={(value) =>
              handleValueChange(
                field.key,
                value,
                necessityAndProportionalityFields.title
              )
            }
            value={dpiaFields[field.key]?.value || ''}
          />
        ))}
      </div>
      <Divider classes="my-[30px]" />
      {/* Risk Assessment and Mitigation Measures */}
      <DynamicFieldGroup
        title={riskAssessmentAndMitigationMeasuresFields.title}
        description={riskAssessmentAndMitigationMeasuresFields.description}
        fields={mapFieldsToDynamicGroup(
          riskAssessmentAndMitigationMeasuresFields.fields
        )}
        onChange={handleRiskAssessmentAndMitigation}
        initialValue={riskAssessmentAndMitigationInitialValue}
      />

      <Divider classes="my-[30px]" />
      {/* Measures to Address Risks */}
      <DynamicFieldGroup
        title={measuresToAddressRisksFields.title}
        description={measuresToAddressRisksFields.description}
        fields={mapFieldsToDynamicGroup(measuresToAddressRisksFields.fields)}
        onChange={handleMeasuresToAddressRisksChange}
        initialValue={measuresToAddressRisksInitialValue}
      />

      <Divider classes="my-[30px]" />
      {/* Consultation and Prior Consultation */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2
          title={consultationAndPriorConsultationFields.title}
          textSize="text-base"
        />
        <Divider />
        {consultationAndPriorConsultationFields.fields.map((field) => (
          <RichTextArea
            key={field.key}
            label={field.label}
            placeholder={field.placeholder}
            onChange={(value) =>
              handleValueChange(
                field.key,
                value,
                consultationAndPriorConsultationFields.title
              )
            }
            value={dpiaFields[field.key]?.value || ''}
          />
        ))}
      </div>

      <Divider classes="my-[30px]" />
      {/* Final Conclusions & Approval */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2
          title={finalConclusionsAndApprovalFields.title}
          textSize="text-base"
        />
        <Divider />

        {/* Overall Assessment Radio Group */}
        <RadioButtonGroup
          title="Overall assessment of residual risk"
          name="overallAssessmentOfResidualRisk"
          options={overallAssessmentOptions.map((opt) => ({
            label: opt.label,
            value: opt.key
          }))}
          selectedValue={dpiaFields[DPIA_OVERALL_ASSESSMENT_KEY]?.value || ''}
          onChange={(value) =>
            handleRadioGroupChange(
              DPIA_OVERALL_ASSESSMENT_KEY,
              value,
              finalConclusionsAndApprovalFields.title
            )
          }
        />

        {/* Final Decision Radio Group */}
        <RadioButtonGroup
          title="Final decision on processing"
          name="finalDecisionOnProcessing"
          options={finalDecisionOptions.map((opt) => ({
            label: opt.label,
            value: opt.key
          }))}
          selectedValue={dpiaFields[DPIA_FINAL_DECISION_KEY]?.value || ''}
          onChange={(value) =>
            handleRadioGroupChange(
              DPIA_FINAL_DECISION_KEY,
              value,
              finalConclusionsAndApprovalFields.title
            )
          }
        />
      </div>

      <ReportFooter componentInfo={footerData} />
    </div>
  );
};
